import { ForbiddenError } from '@ember-data/adapter/error';
import Route from '@ember/routing/route';
import { service } from '@ember/service';

import { isTesting, macroCondition } from '@embroider/macros';
import * as Sentry from '@sentry/ember';
import { forever } from 'ember-concurrency';
import window from 'ember-window-mock';

import {
  apiNamespace,
  companyCreationBaseURL,
  jsURL,
  registerJsURL,
} from 'register-partners-js/constants/hosts';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { ErrorInfo } from 'register-partners-js/utils/error-info';
import { fetchNavigationOrganizations } from 'register-partners-js/utils/fetch-navigation-organizations';

export default class RegistrationsRoute extends Route {
  mainLayout = MainLayout;

  @service deviceManager;
  @service router;
  @service segment;
  @service store;
  @service apiFlowManager;
  @service networkManager;

  async fetchCompanyCreationOrganization() {
    let url = `${companyCreationBaseURL}/${apiNamespace}/organizations`;
    let { data: ccOrganizations = [] } = await this.networkManager.request(url);
    return { ccOrganizations };
  }

  async model() {
    let [pendingPartnerRegistrations, registerOrganizations, appOrganizations] = await Promise.all([
      this.store.query('organization', {
        include: 'registration',
        filter: { registration_status_eq: 'pending' },
      }),
      this.store.findAll('register-organization'),
      this.loadAppOrganizations(),
    ]);

    let partnerRegistrationsViaRegister = registerOrganizations.filter(org =>
      Boolean(org.partnerRegistration.id)
    );

    if (!this.apiFlowManager.isPartnersService) {
      registerOrganizations = registerOrganizations.filter(org => !org.partnerRegistration.id);
    }

    let registerOrganizationsWithPayment = registerOrganizations.filter(org =>
      Boolean(org.depositPaymentId)
    );

    // Navigation API only returns enabled organizations, no filtering needed
    let activeAppOrganizations = appOrganizations;

    let currentRegistrationID = this.store.peekAll('redirection-token')[0]?.registrationID;
    if (!currentRegistrationID) {
      currentRegistrationID = this.store.peekAll('register-redirection-token')[0]?.registrationID;
    }

    let { ccOrganizations } = await this.fetchCompanyCreationOrganization();

    return {
      pendingPartnerRegistrations,
      partnerRegistrationsViaRegister,
      registerOrganizations,
      registerOrganizationsWithPayment,
      activeAppOrganizations,
      currentRegistrationID,
      ccOrganizations: ccOrganizations.filter(org => org.relationships?.creation?.data?.id),
    };
  }

  async afterModel({
    pendingPartnerRegistrations,
    partnerRegistrationsViaRegister,
    registerOrganizations,
    registerOrganizationsWithPayment,
    activeAppOrganizations,
    currentRegistrationID,
  }) {
    let orgsInCapitalDepositState = activeAppOrganizations.filter(this.isCapitalDepositInProgress);
    let orgsNotInCapitalDepositState = activeAppOrganizations.filter(
      org => !this.isCapitalDepositInProgress(org)
    );

    let hasRegisterOrganizationsWithPayment = Boolean(registerOrganizationsWithPayment.length);
    let hasCapitalDepositRegistrations = Boolean(orgsInCapitalDepositState.length);

    if (currentRegistrationID) {
      let otherPendingPartnerRegistrations = pendingPartnerRegistrations.filter(
        org => org.registration.id !== currentRegistrationID
      );
      let otherPartnerRegistrationsViaRegister = partnerRegistrationsViaRegister.filter(
        org => org.partnerRegistration.id !== currentRegistrationID
      );

      let totalOtherRegistrations =
        otherPendingPartnerRegistrations.length + otherPartnerRegistrationsViaRegister.length;

      // If there's only 0 or 1 other registration, prioritize the redirection token
      // This handles the case where LegalPlace creates a new registration (R2)
      // while user has an old pending registration (R1)
      // But if there are 2+ other registrations, show resume page to let user choose
      if (
        totalOtherRegistrations <= 1 &&
        !hasRegisterOrganizationsWithPayment &&
        !hasCapitalDepositRegistrations
      ) {
        return this.router.transitionTo('registration', currentRegistrationID);
      }
    }

    // In this case we want the user to continue registration that he started
    let hasOnlySinglePartnerRegistration =
      (pendingPartnerRegistrations.length === 1 || partnerRegistrationsViaRegister.length === 1) &&
      !hasRegisterOrganizationsWithPayment &&
      !hasCapitalDepositRegistrations;

    if (hasOnlySinglePartnerRegistration) {
      let [organization] = pendingPartnerRegistrations.length
        ? pendingPartnerRegistrations
        : partnerRegistrationsViaRegister;

      let id = pendingPartnerRegistrations.length
        ? organization.registration.id
        : organization.partnerRegistration.id;

      return this.router.replaceWith('registration', id);
    }

    let hasPendingPartnerRegistrations = Boolean(
      pendingPartnerRegistrations.length || partnerRegistrationsViaRegister.length
    );
    let hasRegisterOrganizations = Boolean(registerOrganizations.length);

    // For some reason user went to "incorrect page" and tried to use his Qonto account credentials
    // there are no registrations so user is redirected to the app
    let hasOnlyActiveAppOrganizations =
      !hasPendingPartnerRegistrations &&
      !hasRegisterOrganizations &&
      !orgsInCapitalDepositState.length &&
      orgsNotInCapitalDepositState.length;

    if (hasOnlyActiveAppOrganizations) {
      let [organization] = orgsNotInCapitalDepositState;
      if (this.deviceManager.isMobile || this.deviceManager.isWebview) {
        this.router.replaceWith('next-steps');
      } else {
        window.location.replace(`${jsURL}/organizations/${organization.slug}`);
      }

      if (macroCondition(!isTesting())) {
        await forever;
      }

      return;
    }

    // User has only register organizations before payment
    // He probably made a mistake by entering partners.qonto.com
    // we redirect the user back to welcome.qonto.com so he can continue his register registration
    let hasOnlyRegisterRegistrationsBeforePayment =
      !hasPendingPartnerRegistrations &&
      !orgsInCapitalDepositState.length &&
      !hasRegisterOrganizationsWithPayment &&
      registerOrganizations.length;

    if (hasOnlyRegisterRegistrationsBeforePayment) {
      window.location.replace(`${registerJsURL}/start`);

      if (macroCondition(!isTesting())) {
        await forever;
      }

      return;
    }

    // user has multiple register or / and partner or / and app organizations (in capital deposit state)
    // we want them to see resume page and pick the registration they would like to continue with
    this.segment.track('onboarding_partner_register_accounts_launcher', {
      is_mobile_browser: this.deviceManager.isMobile,
      is_webview: this.deviceManager.isWebview,
    });
  }

  isCapitalDepositInProgress(organization) {
    return organization.underRegistration;
  }

  async loadAppOrganizations() {
    try {
      return await fetchNavigationOrganizations(this.networkManager);
    } catch (error) {
      // similarly to qonto-register-js:
      // since the api returns 403 if no organizations are found,
      // we ignore this error and return empty array
      if (error instanceof ForbiddenError) {
        return [];
      }

      if (ErrorInfo.for(error).shouldSendToSentry) {
        Sentry.captureException(error);
      }
    }

    return [];
  }
}
